import { ChatBubbleLeftRightIcon, ClockIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, PaperAirplaneIcon, PlusIcon, StopIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport, type UIMessage } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar } from '../catalyst/avatar'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { API_BASE } from '../lib/api'
import { deleteConversation, getConversation, listConversations, type ConversationSummary } from '../lib/conversations'
import { Shell } from './shell'

// The Ask/chat page (djot task 8.7, node_01m36r20c5fn7sn24pwwtzy5hb): a single global chat over
// POST /orgs/:orgId/ask (task 8.6), which streams an AI SDK UI-message response — useChat consumes
// it directly, including its search_pesticide_labels tool calls (task 8.5), which this page renders
// as visible "Searching labels…" steps and, once a search completes, as citation chips linking back
// to the cited product's page. The citation UI is the guardrail made visible, not just a backend
// prompt constraint: PRODUCT.md §9 requires every claim to be checkable against the real label.

type SearchResult = {
  productId: string
  productName: string | null
  registrationNumber: string | null
  sectionType: string
  headingText: string | null
  excerpt: string
  relevance: number
}

// The tool runner (server/src/core/tools.ts, runTool) wraps every tool's own return value as
// { ok, effect, data } — a read tool's real output lives at output.data, not output directly.
type SearchToolPart = {
  type: string
  state?: string
  output?: { ok: true; data?: { results?: SearchResult[] } } | { ok: false; error?: { message?: string } }
}

function isSearchToolPart(part: { type: string }): part is SearchToolPart {
  return part.type === 'tool-search_pesticide_labels' || part.type === 'dynamic-tool'
}

function searchResultsOf(part: SearchToolPart): SearchResult[] {
  return part.output?.ok ? (part.output.data?.results ?? []) : []
}

const SECTION_LABELS: Record<string, string> = {
  directions_for_use: 'Directions for use',
  precautions: 'Precautions',
  first_aid: 'First aid',
  toxicological_information: 'Toxicological information',
  environmental_hazards: 'Environmental hazards',
  storage_and_disposal: 'Storage & disposal',
  resistance_management: 'Resistance management',
  mixing_instructions: 'Mixing instructions',
  guarantee: 'Guarantee',
  general: 'General',
}

// Minimal markdown rendering for chat answers: bold/italic/code inline, bullet lists, blockquotes
// (the model often quotes a label excerpt this way) and `---` rules. Not a full CommonMark parser —
// just enough of what a Gemini answer actually produces, so it doesn't render literal `**`/`>`/`*`.
const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(INLINE_PATTERN).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={`${keyPrefix}-${i}`} className="rounded bg-zinc-950/5 px-1 py-0.5 text-[0.9em] dark:bg-white/10">
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 1) return <em key={`${keyPrefix}-${i}`}>{part.slice(1, -1)}</em>
    return part
  })
}

function MarkdownLite({ text }: { text: string }) {
  const blocks = text.trim().split(/\n{2,}/)
  return (
    <div className="space-y-3">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter((l) => l.trim().length > 0)
        if (block.trim() === '---') return <hr key={bi} className="border-zinc-950/10 dark:border-white/10" />
        if (lines.length > 0 && lines.every((l) => /^[-*]\s+/.test(l.trim()))) {
          return (
            <ul key={bi} className="list-disc space-y-1 pl-5">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.trim().replace(/^[-*]\s+/, ''), `${bi}-${li}`)}</li>
              ))}
            </ul>
          )
        }
        if (lines.length > 0 && lines.every((l) => l.trim().startsWith('>'))) {
          return (
            <blockquote key={bi} className="border-l-2 border-zinc-950/15 pl-3 italic text-zinc-600 dark:border-white/15 dark:text-zinc-400">
              {lines.map((l, li) => (
                <p key={li}>{renderInline(l.trim().replace(/^>\s?/, ''), `${bi}-${li}`)}</p>
              ))}
            </blockquote>
          )
        }
        return (
          <p key={bi} className="whitespace-pre-wrap">
            {renderInline(block, `${bi}`)}
          </p>
        )
      })}
    </div>
  )
}

const SUGGESTIONS = [
  'What is the pre-harvest interval for Switch on blueberries?',
  "What's the re-entry interval for Admire 240?",
  'What PPE is required when mixing and loading Altacor?',
]

/** One chip per (product, section) — the search tool returns several chunks per section, which
 *  would otherwise render as visually-identical duplicate chips. */
function dedupeCitations(results: SearchResult[]): SearchResult[] {
  const seen = new Set<string>()
  const deduped: SearchResult[] = []
  for (const r of results.filter((r) => r.productName)) {
    const key = `${r.productId}:${r.sectionType}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(r)
  }
  return deduped
}

function Citations({ results }: { results: SearchResult[] }) {
  const cited = dedupeCitations(results)
  if (cited.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {cited.map((r, i) => (
        <Link
          key={`${r.productId}-${i}`}
          href={`/pesticides/${r.productId}`}
          className="group inline-flex items-center gap-1.5 rounded-full border border-zinc-950/10 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-600 hover:border-zinc-950/20 hover:bg-zinc-100 dark:border-white/10 dark:bg-white/5 dark:text-zinc-400 dark:hover:bg-white/10"
          title={r.excerpt}
        >
          <MagnifyingGlassIcon className="size-3 shrink-0" />
          <span className="font-medium text-zinc-800 dark:text-zinc-200">{r.productName}</span>
          <span className="text-zinc-400 dark:text-zinc-500">· {SECTION_LABELS[r.sectionType] ?? r.sectionType}</span>
        </Link>
      ))}
    </div>
  )
}

function SearchingIndicator() {
  return (
    <div className="mt-2 flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
      <MagnifyingGlassIcon className="size-4 animate-pulse" />
      Searching pesticide labels…
    </div>
  )
}

function MessageBubble({ message }: { message: { id: string; role: string; parts: { type: string; text?: string; state?: string; output?: unknown }[] } }) {
  const isUser = message.role === 'user'
  const text = message.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('')

  const searchParts = message.parts.filter(isSearchToolPart)
  const pendingSearch = searchParts.some((p) => p.state !== 'output-available' && p.state !== 'output-error')
  const results = searchParts.flatMap(searchResultsOf)
  const noCoverage = !isUser && searchParts.length > 0 && !pendingSearch && results.length === 0

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <Avatar
        className="mt-0.5 size-8 shrink-0"
        initials={isUser ? 'You' : 'FA'}
        square={!isUser}
        alt={isUser ? 'You' : 'Farmula assistant'}
      />
      <div className={`min-w-0 flex-1 ${isUser ? 'flex flex-col items-end' : ''}`}>
        <div
          className={
            isUser
              ? 'inline-block max-w-[85%] rounded-2xl rounded-tr-sm bg-zinc-900 px-4 py-2.5 text-white dark:bg-white dark:text-zinc-900'
              : 'max-w-[85%]'
          }
        >
          {text ? (
            isUser ? (
              <Text className="whitespace-pre-wrap !text-white dark:!text-zinc-900">{text}</Text>
            ) : (
              <div className="text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                <MarkdownLite text={text} />
              </div>
            )
          ) : pendingSearch ? null : !isUser ? (
            <Text className="text-zinc-400 dark:text-zinc-500">…</Text>
          ) : null}
        </div>
        {pendingSearch && <SearchingIndicator />}
        {noCoverage && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
            <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-800 dark:text-amber-300">No matching label text was found for this question.</span>
          </div>
        )}
        {!isUser && <Citations results={results} />}
      </div>
    </div>
  )
}

function formatWhen(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' })
}

function HistoryPanel({
  conversations,
  loading,
  activeId,
  className,
  onSelect,
  onNew,
  onDelete,
}: {
  conversations: ConversationSummary[]
  loading: boolean
  activeId: string | null
  className: string
  onSelect: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
}) {
  return (
    <aside className={`min-h-0 w-full shrink-0 flex-col border-zinc-950/5 md:w-64 md:border-r dark:border-white/10 ${className}`} aria-label="Chat history">
      <div className="p-3">
        <Button className="w-full" onClick={onNew}>
          <PlusIcon />
          New chat
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {loading && conversations.length === 0 ? (
          <Text className="px-2 py-2 text-xs">Loading…</Text>
        ) : conversations.length === 0 ? (
          <Text className="px-2 py-2 text-xs">Your past chats will appear here.</Text>
        ) : (
          <ul className="space-y-0.5">
            {conversations.map((c) => (
              <li key={c.id} className="group relative">
                <button
                  type="button"
                  onClick={() => onSelect(c.id)}
                  aria-current={c.id === activeId ? 'page' : undefined}
                  className={`block w-full rounded-lg px-2.5 py-2 pr-9 text-left hover:bg-zinc-950/5 dark:hover:bg-white/5 ${
                    c.id === activeId ? 'bg-zinc-950/5 dark:bg-white/10' : ''
                  }`}
                >
                  <span className="block truncate text-sm/5 font-medium text-zinc-900 dark:text-white">{c.title}</span>
                  <span className="block text-xs/5 text-zinc-500 dark:text-zinc-400">{formatWhen(c.lastMessageAt)}</span>
                </button>
                <button
                  type="button"
                  aria-label={`Delete chat: ${c.title}`}
                  onClick={() => onDelete(c.id)}
                  className="absolute top-2 right-1.5 rounded p-1 text-zinc-400 opacity-0 hover:bg-zinc-950/5 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-white/10"
                >
                  <TrashIcon className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}

type Session = { key: string; conversationId: string | null; initialMessages: UIMessage[] }

let sessionCounter = 0
const newSession = (conversationId: string | null = null, initialMessages: UIMessage[] = []): Session => ({
  key: `session-${++sessionCounter}`,
  conversationId,
  initialMessages,
})

function conversationIdOf(message: UIMessage | undefined): string | null {
  const id = (message?.metadata as { conversationId?: unknown } | undefined)?.conversationId
  return typeof id === 'string' ? id : null
}

/** One chat. Remounted (via `key`) whenever a different conversation is opened. */
function AskChat({
  orgId,
  conversationId,
  initialMessages,
  onConversationStarted,
  onTurnFinished,
}: {
  orgId: string
  conversationId: string | null
  initialMessages: UIMessage[]
  onConversationStarted: (id: string) => void
  onTurnFinished: () => void
}) {
  const listEndRef = useRef<HTMLDivElement>(null)
  const [input, setInput] = useState('')
  // The server owns the transcript: it is told which conversation this is and sent ONLY the new message.
  const conversationIdRef = useRef(conversationId)

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/orgs/${orgId}/ask`,
        credentials: 'include',
        prepareSendMessagesRequest: ({ messages }) => {
          const last = messages[messages.length - 1]
          const text = (last?.parts ?? []).filter((p): p is { type: 'text'; text: string } => p.type === 'text').map((p) => p.text).join('')
          return { body: { conversationId: conversationIdRef.current ?? undefined, message: { parts: [{ type: 'text', text }] } } }
        },
      }),
    [orgId]
  )

  const { messages, sendMessage, status, error, stop } = useChat({ transport, messages: initialMessages })

  useEffect(() => {
    const started = conversationIdOf(messages[messages.length - 1])
    if (started && conversationIdRef.current !== started) {
      conversationIdRef.current = started
      onConversationStarted(started)
    }
  }, [messages, onConversationStarted])

  const busy = status === 'submitted' || status === 'streaming'
  const wasBusy = useRef(false)
  useEffect(() => {
    if (wasBusy.current && !busy) onTurnFinished()
    wasBusy.current = busy
  }, [busy, onTurnFinished])

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, status])

  function submit(text: string) {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    sendMessage({ text: trimmed })
    setInput('')
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit(input)
    }
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-white/10">
              <ChatBubbleLeftRightIcon className="size-6 text-zinc-500 dark:text-zinc-400" />
            </div>
            <Heading level={2} className="mt-4 text-base">
              Ask about a pesticide product
            </Heading>
            <Text className="mt-1 max-w-sm">
              Every answer is grounded in the actual label text, with citations back to the product page. Always confirm
              against the current physical label before applying.
            </Text>
            <div className="mt-6 flex max-w-lg flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s)}
                  className="rounded-full border border-zinc-950/10 px-3 py-1.5 text-sm text-zinc-600 hover:border-zinc-950/20 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-white/5"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-6">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {error && (
              <div className="flex items-start gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm dark:border-red-900 dark:bg-red-950/40">
                <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
                <span className="text-red-800 dark:text-red-300">{error.message || 'Something went wrong. Try again.'}</span>
              </div>
            )}
            <div ref={listEndRef} />
          </div>
        )}
      </div>

      <div className="border-t border-zinc-950/5 px-6 py-4 dark:border-white/10">
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <div className="flex-1">
            <Textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a product's rate, timing, PHI, REI, mixing or safety…"
              rows={1}
              resizable={false}
              className="max-h-40"
            />
          </div>
          {busy ? (
            <Button color="zinc" onClick={() => stop()}>
              <StopIcon />
              Stop
            </Button>
          ) : (
            <Button onClick={() => submit(input)} disabled={!input.trim()}>
              <PaperAirplaneIcon />
              Send
            </Button>
          )}
        </div>
        <Text className="mx-auto mt-2 max-w-3xl text-xs">
          Advisory information only. Always confirm against the current physical product label before applying.
        </Text>
      </div>
    </>
  )
}

export function AskPage() {
  const { org } = useAuth()
  const orgId = org?.id
  const { conversationId: routeId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()

  const [session, setSession] = useState<Session>(() => newSession())
  const sessionIdRef = useRef<string | null>(null)
  sessionIdRef.current = session.conversationId
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [listLoading, setListLoading] = useState(true)
  const refreshList = useCallback(() => {
    if (!orgId) return
    listConversations(orgId)
      .then((page) => setConversations(page.data))
      .catch(() => {})
      .finally(() => setListLoading(false))
  }, [orgId])
  useEffect(refreshList, [refreshList])

  // The URL is the source of truth for WHICH chat is open. A chat this page just created already
  // matches its own new URL, so it is not reloaded (that would cut the stream that made it).
  useEffect(() => {
    if (!orgId) return
    if (routeId === (sessionIdRef.current ?? undefined)) return
    setOpenError(null)
    if (!routeId) {
      setSession(newSession())
      return
    }
    let cancelled = false
    setOpening(true)
    getConversation(orgId, routeId)
      .then((c) => !cancelled && setSession(newSession(c.id, c.messages)))
      .catch((err: unknown) => !cancelled && setOpenError(err instanceof Error ? err.message : 'Could not open this chat.'))
      .finally(() => !cancelled && setOpening(false))
    return () => {
      cancelled = true
    }
  }, [orgId, routeId])

  const started = useCallback(
    (id: string) => {
      setSession((s) => ({ ...s, conversationId: id }))
      sessionIdRef.current = id
      navigate(`/ask/${id}`, { replace: true })
      refreshList()
    },
    [navigate, refreshList]
  )

  function remove(id: string) {
    if (!orgId || !window.confirm('Delete this chat from your history?')) return
    deleteConversation(orgId, id)
      .then(() => {
        setConversations((list) => list.filter((c) => c.id !== id))
        if (id === session.conversationId) navigate('/ask')
      })
      .catch(() => {})
  }

  const go = (path: string) => {
    setShowHistory(false)
    navigate(path)
  }

  return (
    <Shell flush>
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center justify-between gap-4 border-b border-zinc-950/5 px-6 py-4 dark:border-white/10">
          <div className="min-w-0">
            <Heading>Ask Farmula</Heading>
            <Text>Questions about registered pesticide products, grounded in real Health Canada label text.</Text>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button className="md:hidden" outline onClick={() => setShowHistory((v) => !v)}>
              <ClockIcon />
              History
            </Button>
            <Badge color="zinc" className="max-sm:hidden">
              Blueberry, BC
            </Badge>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <HistoryPanel
            conversations={conversations}
            loading={listLoading}
            activeId={session.conversationId}
            className={showHistory ? 'flex' : 'hidden md:flex'}
            onSelect={(id) => go(`/ask/${id}`)}
            onNew={() => go('/ask')}
            onDelete={remove}
          />
          <div className={`min-w-0 flex-1 flex-col ${showHistory ? 'hidden md:flex' : 'flex'}`}>
            {!orgId || opening ? (
              <div className="flex flex-1 items-center justify-center">
                <Text>Loading…</Text>
              </div>
            ) : openError ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                <Text>{openError}</Text>
                <Button onClick={() => go('/ask')}>Start a new chat</Button>
              </div>
            ) : (
              <AskChat
                key={session.key}
                orgId={orgId}
                conversationId={session.conversationId}
                initialMessages={session.initialMessages}
                onConversationStarted={started}
                onTurnFinished={refreshList}
              />
            )}
          </div>
        </div>
      </div>
    </Shell>
  )
}
